async function getCollectionProducts(collectionHandle) {
    const url = `/collections/${collectionHandle}/products.json?limit=250`;
    
    try {
        const response = await fetch(url);
        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }
        const data = await response.json();
        return data.products;
    } catch (error) {
        console.error('Failed to fetch products:', error);
        return [];
    }
}

// Debounce function to prevent excessive API calls
function debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
        const later = () => {
            clearTimeout(timeout);
            func(...args);
        };
        clearTimeout(timeout);
        timeout = setTimeout(later, wait);
    };
}

// Update search count display
function updateSearchCount(count, query) {
    let countElement = document.getElementById('search-count-display');
    
    if (!countElement) {
        // Create count element and append to .collection-search_wrap
        const searchWrap = document.querySelector('.collection-only-search_wrap');
        if (searchWrap) {
            countElement = document.createElement('div');
            countElement.id = 'search-count-display';
            countElement.className = 'search-count-display';
            searchWrap.appendChild(countElement);
        }
    }
    
    if (countElement) {
        if (count > 0) {
            countElement.innerHTML = `Found ${count} product${count !== 1 ? 's' : ''} for "${query}"`;
            countElement.style.display = 'block';
        } else if (query.length >= 3) {
            countElement.innerHTML = `No products found for "${query}"`;
            countElement.style.display = 'block';
        } else {
            countElement.style.display = 'none';
        }
    }
}

// Clear search completely
function clearSearch() {
    const searchInput = document.getElementById('collection-search');
    const resultsContainer = document.getElementById('collection-search-results');
    const clearButton = document.getElementById('collection-search-clear');
    
    if (searchInput) {
        searchInput.value = '';
        searchInput.focus();
    }
    if (resultsContainer) {
        resultsContainer.innerHTML = '';
        resultsContainer.parentElement.classList.remove('active');
    }
    if (clearButton) {
        clearButton.style.display = 'none';
    }
    clearSearchCount();
}

// Show/hide clear button
function toggleClearButton(show) {
    let clearButton = document.getElementById('collection-search-clear');
    
    if (!clearButton) {
        // Create clear button and append under .collection-search-box
        const searchBox = document.querySelector('.collection-search-box');
        if (searchBox) {
            clearButton = document.createElement('button');
            clearButton.id = 'collection-search-clear';
            clearButton.type = 'button';
            clearButton.innerHTML = '✕';
            clearButton.title = 'Clear search';
            clearButton.style.cssText = `
                position: absolute;
                right: 8px;
                top: 50%;
                transform: translateY(-50%);
                background: none;
                border: none;
                font-size: 16px;
                cursor: pointer;
                padding: 4px;
                color: #999;
                display: none;
            `;
            
            // Position search box relatively if not already
            if (getComputedStyle(searchBox).position === 'static') {
                searchBox.style.position = 'relative';
            }
            
            searchBox.appendChild(clearButton);
            
            // Add click handler
            clearButton.addEventListener('click', clearSearch);
        }
    }
    
    if (clearButton) {
        clearButton.style.display = show ? 'block' : 'none';
    }
}

function clearSearchCount() {
    const countElement = document.getElementById('search-count-display');
    if (countElement) {
        countElement.style.display = 'none';
    }
}

function showSpinner(container) {
    container.innerHTML = `
        <li class="search-spinner">
            <div class="spinner-wrapper">
                <div class="spinner"></div>
                <span>Searching...</span>
            </div>
        </li>
    `;
    container.parentElement.classList.add('active');
}

// Hide loading spinner
function hideSpinner(container) {
    const spinner = container.querySelector('.search-spinner');
    if (spinner) {
        spinner.remove();
    }
}

// Badge logic similar to Liquid template with new conditions
function generateProductBadges(product, settings = {}) {
    const badges = [];
    let badgeClasses = [];
    let dataAttributes = {};
    
    // Default settings (you can customize these)
    const defaultSettings = {
        show_sale_badge: true,
        show_sold_out_badge: false,
        show_custom_badge: false,
        show_bundle_badge: false,
        show_new_badge: true,
        sale_badge_type: 'text', // 'text', 'discount', 'text_discount'
        new_badge_type: 'auto', // 'auto' or 'tag'
        new_badge_limit: 30,
        new_badge_time: 30, // days
        badge_position: 'left'
    };
    
    const config = { ...defaultSettings, ...settings };
    
    // Helper function to calculate discount percentage
    function calculateDiscount(comparePrice, price) {
        if (!comparePrice || comparePrice <= price) return 0;
        return Math.round(((comparePrice - price) * 100.0) / comparePrice);
    }
    
    // Sale Badge Logic
    let sale_badge = false;
    if (config.show_sale_badge && product.variants && product.variants.length > 0) {
        // Check if any variant has a compare_at_price > price
        sale_badge = product.variants.some(variant => 
            variant.compare_at_price && variant.compare_at_price > variant.price
        );
    }
    
    // Sold Out Badge Logic
    let sold_out_badge = false;
    if (config.show_sold_out_badge) {
        sold_out_badge = !product.variants[0].available;
    }
    
    // Custom Badge Logic
    let custom_badge = false;
    let custom_badge_text = '';
    if (config.show_custom_badge) {
        // Check for 'label' tag
        if (product.tags && product.tags.some(tag => tag.toLowerCase().replace(/\s+/g, '-') === 'label')) {
            custom_badge = true;
        }
        
        // Check for custom badge metafield (if available in JSON)
        if (product.metafields && product.metafields.custom && product.metafields.custom.custom_badge) {
            custom_badge = true;
            custom_badge_text = product.metafields.custom.custom_badge;
        }
    }
    
    // Bundle Badge Logic
    let bundle_badge = false;
    if (config.show_bundle_badge) {
        if (product.metafields && product.metafields.c_f && product.metafields.c_f.grouped_sub_product) {
            bundle_badge = true;
        }
    }
    
    // New Badge Logic
    let new_badge = false;
    if (config.show_new_badge) {
        if (config.new_badge_type === 'auto') {
            if (product.published_at) {
                const publishedDate = new Date(product.published_at);
                const now = new Date();
                const diffTime = Math.abs(now - publishedDate);
                const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
                
                if (diffDays <= config.new_badge_time) {
                    new_badge = true;
                }
            }
        } else {
            // Check for 'new' tag
            if (product.tags && product.tags.some(tag => tag.toLowerCase().replace(/\s+/g, '-') === 'new')) {
                new_badge = true;
            }
        }
    }
    
    // NEW CONDITIONS: Apply badge visibility rules
    if (sold_out_badge) {
        // If out of stock, hide both sale and new badges
        sale_badge = false;
        new_badge = false;
    } else if (sale_badge) {
        // If on sale, hide new badge
        new_badge = false;
    }
    
    // Generate badge HTML if any badges are active
    if (sale_badge || sold_out_badge || custom_badge || bundle_badge || new_badge) {
        // Calculate date attributes for CSS classes
        const dateNow = Math.floor(Date.now() / 1000);
        const datePublished = product.published_at ? Math.floor(new Date(product.published_at).getTime() / 1000) : dateNow;
        const dateMinus = dateNow - datePublished;
        const dateDifference = Math.floor(dateMinus / 86400);
        
        // Build CSS classes
        let cssClasses = `card__badge badge-${config.badge_position} halo-productBadges halo-productBadges--${config.badge_position} date-${dateMinus} date1-${dateDifference}`;
        
        if (config.sale_badge_type === 'discount' || config.sale_badge_type === 'text_discount') {
            cssClasses += ' has-badge-js';
        }
        
        if (!sale_badge) {
            cssClasses += ' sale_badge_disable';
        }
        
        // Build data attributes
        if (config.sale_badge_type === 'text_discount' || config.sale_badge_type === 'text') {
            dataAttributes['data-text-sale-badge'] = 'Sale';
        }
        if (config.sale_badge_type === 'discount') {
            dataAttributes['data-text-sale-badge'] = '- ';
        }
        dataAttributes['data-new-badge-number'] = config.new_badge_limit;
        
        // Build data attribute string
        const dataAttrString = Object.entries(dataAttributes)
            .map(([key, value]) => `${key}="${value}"`)
            .join(' ');
        
        let badgeHtml = `<div class="${cssClasses}" ${dataAttrString}>`;
        
        // New Badge
        if (new_badge) {
            badgeHtml += '<span class="badge new-badge" aria-hidden="true">New</span>';
        }
        
        // Sale Badge
        if (sale_badge) {
            let saleText = 'Sale';
            
            if (config.sale_badge_type === 'discount' || config.sale_badge_type === 'text_discount') {
                // Calculate maximum discount across all variants
                let maxDiscount = 0;
                if (product.variants && product.variants.length > 0) {
                    product.variants.forEach(variant => {
                        if (variant.compare_at_price && variant.compare_at_price > variant.price) {
                            const discount = calculateDiscount(variant.compare_at_price, variant.price);
                            if (discount > maxDiscount) {
                                maxDiscount = discount;
                            }
                        }
                    });
                }
                
                if (config.sale_badge_type === 'discount') {
                    saleText = `-${maxDiscount}%`;
                } else if (config.sale_badge_type === 'text_discount') {
                    saleText = `Sale ${maxDiscount}%`;
                }
            }
            
            badgeHtml += `<span class="badge sale-badge" aria-hidden="true">${saleText}</span>`;
        }
        
        // Sold Out Badge
        if (sold_out_badge) {
            badgeHtml += '<span class="badge sold-out-badge" aria-hidden="true">Sold</span>';
        }
        
        // Custom Badge
        if (custom_badge) {
            const customText = custom_badge_text || 'Custom';
            badgeHtml += `<span class="badge custom-badge" aria-hidden="true">${customText}</span>`;
        }
        
        // Bundle Badge
        if (bundle_badge) {
            badgeHtml += '<span class="badge bundle-badge" aria-hidden="true">Bundle</span>';
        }
        
        badgeHtml += '</div>';
        
        return badgeHtml;
    }
    
    return '';
}

// Currency configuration - customize this based on your store settings
const CURRENCY_CONFIG = {
    symbol: '$',
    position: 'before', // 'before' or 'after'
    space: false, // true to add space between symbol and amount
    decimals: 2
};

// Helper function to format price with currency symbol
function formatPriceWithCurrency(price, config = CURRENCY_CONFIG) {
    if (!price && price !== 0) return 'Price unavailable';
    
    let numericPrice;
    if (typeof price === 'string') {
        // If price already contains currency symbol, return as is
        if (price.includes(config.symbol)) {
            return price;
        }
        // Extract numeric value from string
        numericPrice = parseFloat(price.replace(/[^0-9.]/g, '') || '0');
    } else {
        numericPrice = price;
    }
    
    // Convert from cents to dollars if needed (assuming price is in cents)
    const dollarAmount = numericPrice >= 100 ? (numericPrice / 100) : numericPrice;
    const formattedAmount = dollarAmount.toFixed(config.decimals);
    
    const space = config.space ? ' ' : '';
    
    if (config.position === 'before') {
        return `${config.symbol}${space}${formattedAmount}`;
    } else {
        return `${formattedAmount}${space}${config.symbol}`;
    }
}

// Render product price with proper sale/regular price structure and currency symbols
function renderProductPrice(product) {
    if (!product.variants || product.variants.length === 0) {
        return '<div class="price"><dl><div class="price__regular"><dd class="price__last"><span class="price-item price-item--regular">Price unavailable</span></dd></div></dl></div>';
    }
    
    const variant = product.variants[0];
    
    // Check if variant has valid price data
    if (!variant || (!variant.price && variant.price !== 0)) {
        return '<div class="price"><dl><div class="price__regular"><dd class="price__last"><span class="price-item price-item--regular">Price unavailable</span></dd></div></dl></div>';
    }
    
    // Helper function to safely get numeric value from price
    function getNumericPrice(price) {
        if (!price && price !== 0) return 0;
        if (typeof price === 'number') return price;
        const numericValue = parseFloat(String(price).replace(/[^0-9.]/g, '') || '0');
        return isNaN(numericValue) ? 0 : numericValue;
    }
    
    const priceValue = getNumericPrice(variant.price);
    const comparePriceValue = getNumericPrice(variant.compare_at_price);
    
    const hasComparePrice = variant.compare_at_price && 
                           comparePriceValue > 0 && 
                           comparePriceValue > priceValue;
    
    // Format prices with currency symbols
    const formattedPrice = formatPriceWithCurrency(variant.price);
    const formattedComparePrice = formatPriceWithCurrency(variant.compare_at_price);
    
    // Helper function to get price value for data attributes (in cents)
    function getPriceValueCents(price) {
        const numericPrice = getNumericPrice(price);
        // If price is already in cents (>= 100), use as is, otherwise convert
        return numericPrice >= 100 ? Math.round(numericPrice) : Math.round(numericPrice * 100);
    }
    
    const priceValueCents = getPriceValueCents(variant.price);
    const comparePriceValueCents = getPriceValueCents(variant.compare_at_price);
    
    if (hasComparePrice) {
        // On sale - show both regular and sale price
        return `
            <div class="price price--on-sale">
                <dl>
                    <div class="price__regular">
                        <dd class="price__last">
                            <span class="price-item price-item--regular">${formattedPrice}</span>
                        </dd>
                    </div>
                    <div class="price__sale">
                        <dd class="price__compare" data-compare="${comparePriceValueCents}">
                            <s class="price-item price-item--regular">${formattedComparePrice}</s>
                        </dd>
                        <dd class="price__last" data-last="${priceValueCents}">
                            <span class="price-item price-item--sale">${formattedPrice}</span>
                        </dd>
                    </div>
                    <small class="unit-price caption hidden">
                        <dt class="visually-hidden">Unit price</dt>
                        <dd class="price__last">
                            <span></span>
                            <span aria-hidden="true">/</span>
                            <span class="visually-hidden">&nbsp;per&nbsp;</span>
                            <span></span>
                        </dd>
                    </small>
                </dl>
            </div>
        `;
    } else {
        // Regular price only
        return `
            <div class="price">
                <dl>
                    <div class="price__regular">
                        <dd class="price__last">
                            <span class="price-item price-item--regular">${formattedPrice}</span>
                        </dd>
                    </div>
                    <small class="unit-price caption hidden">
                        <dt class="visually-hidden">Unit price</dt>
                        <dd class="price__last">
                            <span></span>
                            <span aria-hidden="true">/</span>
                            <span class="visually-hidden">&nbsp;per&nbsp;</span>
                            <span></span>
                        </dd>
                    </small>
                </dl>
            </div>
        `;
    }
}

// Render product card
function renderProduct(product) {
    const product_id = product.variants[0].product_id;
    const variant_id = product.variants[0].id;
    const product_handle = product.handle;
    const imageUrl = product.images && product.images.length > 0 ? product.images[0].src : '/assets/no-image.png';
    
    // Generate badges using the logic similar to Liquid template
    const badgeHtml = generateProductBadges(product, BADGE_SETTINGS);
    
    return `
        <li class="product">
            <div class="product-item" data-product-id="${product_id}">
                <div class="card">
                    <div class="card-product">
                        <div class="card-product__wrapper">
                            ${badgeHtml}
                            
                            <div class="card-media card-media--portrait media--loading-effect" style="padding-bottom: 130%;">
                                <img class="motion-reduce lazy" src="${imageUrl}" alt="${product.title}" loading="lazy">
                                <a class="card-link" href="/products/${product_handle}" title="${product.title}"></a>
                            </div>
                        </div>
                    </div>
                    <div class="card-information">
                        <div class="card-information__wrapper text-center">
                            <a class="card-title link-underline card-title-ellipsis"
                                href="/products/${product_handle}"
                                data-product-title="${product.title}"
                                data-product-url="/products/${product_handle}">
                                <span class="text">${product.title}</span>
                            </a>
                            
                            ${product.desc ? `<div class="card-summary card-list__hidden">${product.desc}</div>` : ''}
                            
                            <div class="card-price">
                                ${renderProductPrice(product)}
                            </div>
                        </div>
                        <div class="card-action">
                            <div class="custom_card-qty" data-update-quantity="">
                                <form action="/cart/add" method="post" class="variants"
                                    id="form-${product_id}-template--25111127458075__product-grid"
                                    data-product-id="${product_id}" enctype="multipart/form-data">
                                    <input type="hidden" name="id" value="${variant_id}">
                                    <div class="card-quantity_wrap">
                                        <card-update-quantity class="previewCartItem-qty quickshop-quantity">
                                            <button type="button" name="minus" data-minus-quantity-quickshop="" class="minus btn-quantity"></button>
                                            <input class="form-input quantity ProductDrop" name="quantity"
                                                id="quickshop-update-${product_id}-template--25111127458075__product-grid"
                                                data-qtt-id1="quantity${product_id}" value="1" type="number"
                                                data-product-card-quantity="" data-product-card-quantity-id="${product_id}">
                                            <button type="button" name="plus" data-plus-quantity-quickshop="" class="plus btn-quantity"></button>
                                        </card-update-quantity>
                                        <button type="submit" name="add" data-btn-addtocart=""
                                            class="product-form__submit button button--secondary"
                                            id="product-add-to-cart-${product_id}">Add to Cart</button>
                                    </div>
                                </form>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </li>
    `;
}

// Main search function
async function performSearch(query, products, resultsContainer) {
    // Clear previous results
    resultsContainer.innerHTML = '';
    
    // Hide results and clear count if less than 3 characters
    if (query.length < 3) {
        resultsContainer.parentElement.classList.remove('active');
        clearSearchCount();
        return;
    }
    
    // Show spinner while searching
    showSpinner(resultsContainer);
    
    try {
        // Simulate search delay (remove in production if not needed)
        await new Promise(resolve => setTimeout(resolve, 300));
        
        // Filter products
        /*const matches = products.filter(product => {
            const titleMatch = product.title.toLowerCase().includes(query);
            const vendorMatch = product.vendor && product.vendor.toLowerCase().includes(query);
            const tagMatch = product.tags && product.tags.some(tag => tag.toLowerCase().includes(query));
            
            return titleMatch || vendorMatch || tagMatch;
        });*/
        function containsWord(text, word) {
            if (!text) return false;
            return text.toLowerCase().includes(word.toLowerCase());
        }
 
        function hasDistributedMatches(product, queryWords) {
            const matchedWords = new Set();
 
            queryWords.forEach(word => {
                if (containsWord(product.title, word)) {
                    matchedWords.add(word);
                    return;
                }
                if (containsWord(product.vendor, word)) {
                    matchedWords.add(word);
                    return;
                }
                if (product.tags && product.tags.some(tag => containsWord(tag, word))) {
                    matchedWords.add(word);
                    return;
                }
            });
 
            return matchedWords.size === queryWords.length;
        }
 
        const matches = products.filter(product => {
            const queryWords = query.toLowerCase()
                .split(' ')
                .filter(word => word.length > 0);
 
            if (queryWords.length === 1) {
                const word = queryWords[0];
                return containsWord(product.title, word) ||
                    containsWord(product.vendor, word) ||
                    (product.tags && product.tags.some(tag => containsWord(tag, word)));
            }
 
            return hasDistributedMatches(product, queryWords);
        });
        
        // Hide spinner
        hideSpinner(resultsContainer);
        
        // Display results
        if (matches.length > 0) {
            const fragment = document.createDocumentFragment();
            matches.forEach(product => {
                const li = document.createElement('li');
                li.innerHTML = renderProduct(product).replace('<li class="product">', '').replace('</li>', '');
                li.className = 'product';
                li.dataset.productId = product.id; // Use product.id
                fragment.appendChild(li);
            });
            
            resultsContainer.innerHTML = '';
            resultsContainer.appendChild(fragment);
            resultsContainer.parentElement.classList.add('active');
            
            // Add result count after search input
            updateSearchCount(matches.length, query);
            
        } else {
            resultsContainer.innerHTML = `
                <li class="search-message no-results">
                    <div class="message-wrapper">
                        <span>No products found for "${query}"</span>
                    </div>
                </li>
            `;
            resultsContainer.parentElement.classList.add('active');
            updateSearchCount(0, query);
        }
        
    } catch (error) {
        console.error('Search error:', error);
        hideSpinner(resultsContainer);
        resultsContainer.innerHTML = `
            <li class="search-error">
                <div class="error-wrapper">
                    <span>Search failed. Please try again.</span>
                </div>
            </li>
        `;
        resultsContainer.parentElement.classList.add('active');
    }
}

// Badge Configuration - Customize these settings to match your theme settings
const BADGE_SETTINGS = {
    show_sale_badge: true,
    show_sold_out_badge: true,
    show_custom_badge: true,
    show_bundle_badge: true,
    show_new_badge: true,
    sale_badge_type: 'text', // 'text', 'discount', 'text_discount'
    new_badge_type: 'auto', // 'auto' or 'tag'
    new_badge_limit: 30,
    new_badge_time: 30, // days
    badge_position: 'left' // 'left' or 'right'
};

(async function initializeSearch() {
    try {
        // Show initial loading state
        const searchInput = document.getElementById('collection-search');
        const resultsContainer = document.getElementById('collection-search-results');
        
        if (!searchInput || !resultsContainer) {
            console.error('Search elements not found');
            return;
        }
        
        // Add loading state while fetching products
        let products = [];
        
        // Check if collection_handle is defined globally
        if (typeof collection_handle !== 'undefined') {
            console.log('Fetching products...');
            products = await getCollectionProducts(collection_handle);
            console.log(`Loaded ${products.length} products`);
        } else {
            console.warn('collection_handle not defined');
        }
        
        // Create debounced search function
        const debouncedSearch = debounce((query) => {
            performSearch(query, products, resultsContainer);
        }, 300);
        
        // Add search functionality with clear button toggle
        searchInput.addEventListener('input', function(e) {
            const query = this.value.toLowerCase().trim();
            toggleClearButton(query.length > 0);
            debouncedSearch(query);
        });
        
        // Clear search on escape key
        searchInput.addEventListener('keydown', function(e) {
            if (e.key === 'Escape') {
                clearSearch();
            }
        });
        
        console.log('Search functionality initialized successfully');
        
    } catch (error) {
        console.error('Failed to initialize search:', error);
    }
})();